import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const AuthCallbackMicrosoft = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    console.log('[AuthCallbackMicrosoft] === Microsoft Graph OAuth Callback ===');
    console.log('[AuthCallbackMicrosoft] Full URL:', window.location.href);
    console.log('[AuthCallbackMicrosoft] Hash:', window.location.hash);
    console.log('[AuthCallbackMicrosoft] Search:', window.location.search);

    const handleCallback = async () => {
      try {
        // Parse both hash (implicit flow) and search params (code flow)
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const searchParams = new URLSearchParams(window.location.search);
        
        const accessToken = hashParams.get('access_token');
        const authCode = searchParams.get('code');
        const qrSessionId = searchParams.get('state'); // QR session ID
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

        console.log('[AuthCallbackMicrosoft] Parsed params:', {
          hasToken: !!accessToken,
          hasCode: !!authCode,
          hasQrSession: !!qrSessionId,
          error,
          errorDescription
        });

        // Handle errors
        if (error) {
          const errorMsg = errorDescription || error;
          console.error('[AuthCallbackMicrosoft] ❌ Auth error:', errorMsg);
          
          window.opener?.postMessage({
            status: 'error',
            error: errorMsg,
            source: 'microsoft-graph-auth'
          }, window.location.origin);
          
          setStatus('error');
          setMessage(`Authentication failed: ${errorMsg}`);
          setTimeout(() => window.close(), 2000);
          return;
        }

        // QR-Auth Flow: Exchange authorization code for token
        if (authCode && qrSessionId) {
          console.log('[AuthCallbackMicrosoft] 🔐 QR-Auth flow detected');
          setMessage('Exchanging authorization code...');
          
          // Load PKCE code verifier from localStorage
          const codeVerifier = localStorage.getItem(`pkce_verifier_${qrSessionId}`);
          
          if (!codeVerifier) {
            console.error('[AuthCallbackMicrosoft] PKCE verifier not found in localStorage');
            throw new Error('PKCE verification failed - verifier not found');
          }
          
          console.log('[AuthCallbackMicrosoft] Found PKCE verifier:', codeVerifier.substring(0, 10) + '...');
          
          const { data: tokenData, error: exchangeError } = await supabase.functions.invoke('exchange-token', {
            body: {
              code: authCode,
              redirect_uri: `${window.location.origin}/auth-callback-microsoft`,
              flow: 'qr',
              code_verifier: codeVerifier
            }
          });

          // Clean up verifier after use
          localStorage.removeItem(`pkce_verifier_${qrSessionId}`);
          console.log('[AuthCallbackMicrosoft] Cleaned up PKCE verifier from localStorage');

          if (exchangeError || !tokenData?.accessToken) {
            throw new Error(exchangeError?.message || 'Failed to exchange code for token');
          }

          console.log('[AuthCallbackMicrosoft] ✅ Token exchange successful');
          setMessage('Fetching user profile...');

          // Fetch Microsoft user profile to get email
          console.log('[AuthCallbackMicrosoft] Fetching Microsoft user profile...');
          const userInfoResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              'Authorization': `Bearer ${tokenData.accessToken}`
            }
          });

          if (!userInfoResponse.ok) {
            throw new Error('Failed to fetch Microsoft user profile');
          }

          const userInfo = await userInfoResponse.json();
          const userEmail = userInfo.mail || userInfo.userPrincipalName;
          console.log('[AuthCallbackMicrosoft] Microsoft user email:', userEmail);

          if (!userEmail) {
            throw new Error('No email found in Microsoft profile');
          }

          setMessage('Creating email2contact account...');

          // Complete QR auth with Microsoft Graph token + User Email
          const { error: completeError } = await supabase.functions.invoke('complete-qr-auth', {
            body: {
              sessionId: qrSessionId,
              graphToken: tokenData.accessToken,
              userEmail: userEmail
            }
          });

          if (completeError) {
            throw new Error(completeError.message || 'Failed to complete QR authentication');
          }

          console.log('[AuthCallbackMicrosoft] ✅ QR authentication completed!');
          setStatus('success');
          setMessage('Authentication successful! You can close this window.');
          return;
        }

        // Direct Login PKCE Flow: Code without QR session (Website Login)
        if (authCode && !qrSessionId) {
          console.log('[AuthCallbackMicrosoft] 🔐 Website Login PKCE flow detected');
          setMessage('Exchanging authorization code...');
          
          // Load PKCE code verifier from sessionStorage (popup context)
          const codeVerifier = sessionStorage.getItem('pkce_verifier_direct_login');
          
          if (!codeVerifier) {
            console.error('[AuthCallbackMicrosoft] PKCE verifier not found in sessionStorage');
            throw new Error('PKCE verification failed - verifier not found');
          }
          
          console.log('[AuthCallbackMicrosoft] Found PKCE verifier:', codeVerifier.substring(0, 10) + '...');
          
          const { data: tokenData, error: exchangeError } = await supabase.functions.invoke('exchange-token', {
            body: {
              code: authCode,
              redirect_uri: `${window.location.origin}/auth-callback-microsoft`,
              flow: 'direct',
              code_verifier: codeVerifier
            }
          });

          // Clean up verifier after use
          sessionStorage.removeItem('pkce_verifier_direct_login');
          console.log('[AuthCallbackMicrosoft] Cleaned up PKCE verifier from sessionStorage');

          if (exchangeError || !tokenData?.accessToken) {
            throw new Error(exchangeError?.message || 'Failed to exchange code for token');
          }

          console.log('[AuthCallbackMicrosoft] ✅ Token exchange successful');
          setMessage('Fetching Microsoft user profile...');

          // Fetch Microsoft user email
          const userInfoResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: { 'Authorization': `Bearer ${tokenData.accessToken}` }
          });

          if (!userInfoResponse.ok) {
            throw new Error('Failed to fetch Microsoft user profile');
          }

          const userInfo = await userInfoResponse.json();
          const userEmail = userInfo.mail || userInfo.userPrincipalName;
          console.log('[AuthCallbackMicrosoft] Microsoft user email:', userEmail);

          if (!userEmail) {
            throw new Error('No email found in Microsoft profile');
          }

          setMessage('Verifying account...');

          // Call edge function to login existing user (won't create new users)
          const { data: sessionData, error: sessionError } = await supabase.functions.invoke('login-existing-user', {
            body: {
              email: userEmail,
              graphToken: tokenData.accessToken
            }
          });

            if (sessionError || sessionData?.error) {
              const errorMessage = sessionData?.error || sessionError?.message || 'Unknown error';
              console.error('[AuthCallbackMicrosoft] Login failed:', errorMessage);
              setStatus('error');
              
              if (errorMessage.includes('Coming Soon')) {
                setMessage('Coming Soon! You cannot create an account just yet.');
              } else {
                setMessage(`Login failed: ${errorMessage}`);
              }
              
              setTimeout(() => window.close(), 5000);
              return;
            }

          if (!sessionData?.session) {
            throw new Error('Failed to create session');
          }

          console.log('[AuthCallbackMicrosoft] ✅ Session created');

          // Set the session
          await supabase.auth.setSession({
            access_token: sessionData.session.access_token,
            refresh_token: sessionData.session.refresh_token
          });

          const userId = sessionData.session.user.id;
          console.log('[AuthCallbackMicrosoft] User logged in:', userId);
          
          // Store Graph token server-side via Edge Function
          console.log('[AuthCallbackMicrosoft] Storing Graph token server-side...');
          const { error: storeError } = await supabase.functions.invoke('store-graph-token', {
            body: {
              graphToken: tokenData.accessToken,
              expiresInSeconds: 3600,
            },
          });

          if (storeError) {
            console.error('[AuthCallbackMicrosoft] Failed to store token:', storeError);
          } else {
            console.log('[AuthCallbackMicrosoft] ✅ Graph token stored server-side');
          }
          
          // Send message to Opener Window (Popup-Parent)
          if (window.opener) {
            window.opener.postMessage({
              type: 'auth-success',
              graphToken: tokenData.accessToken,
              session: {
                access_token: sessionData.session.access_token,
                refresh_token: sessionData.session.refresh_token,
                expires_at: sessionData.session.expires_at,
                expires_in: sessionData.session.expires_in,
                user: sessionData.session.user
              }
            }, window.location.origin);
            
            // Close popup automatically after 2 seconds
            setTimeout(() => {
              window.close();
            }, 2000);
            
            setStatus('success');
            setMessage('✅ Login successful! Redirecting...');
          } else {
            // Fallback: Direct redirect (shouldn't happen with popup)
            const redirectPath = sessionStorage.getItem('auth_redirect') || '/settings';
            sessionStorage.removeItem('auth_redirect');
            setTimeout(() => {
              window.location.href = redirectPath;
            }, 1000);
            
            setStatus('success');
            setMessage('✅ Login successful! Redirecting...');
          }
          return;
        }

        // No token or code received
        console.error('[AuthCallbackMicrosoft] ❌ No token or authorization code received');
        window.opener?.postMessage({
          status: 'error',
          error: 'No access token or authorization code received',
          source: 'microsoft-graph-auth'
        }, window.location.origin);
        
        setStatus('error');
        setMessage('No access token or authorization code received');
        setTimeout(() => window.close(), 2000);
        
      } catch (err) {
        console.error('[AuthCallbackMicrosoft] ❌ Callback processing error:', err);
        setStatus('error');
        setMessage(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        
        window.opener?.postMessage({
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
          source: 'microsoft-graph-auth'
        }, window.location.origin);
        
        setTimeout(() => window.close(), 2000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
            <p className="text-sm text-green-600 font-medium">{message}</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <p className="text-sm text-destructive font-medium">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};
